// Lib
import React, { useMemo } from 'react';
import { Typography } from '@mui/material';

// Components
import Button from '../../Button';
import Feather from '../../Feather';
import FooterButtons from '../../Modal/FooterButtons';
import Addresses from './Addresses';
import FileSelection from './FileSelection';
import Form from '../Form';

// Hooks
import useImportAddresses from '../../../pages/AddressBook/hooks/useImportAddresses';
import useModal from '../../../hooks/useModal';
import useAddressForm from '../../../pages/AddressBook/hooks/useAddressForm';
import useImportAddress from '../../../pages/AddressBook/hooks/useImportAddress';
import ErrorMessage from '../../Error/ErrorMessage';

// Constants
import {
  UPLOAD_STEP,
  REVIEW_STEP,
  IMPORT_STEPS_COUNT,
} from '../../../constants/addressBook';
import { useStyles } from './ImportAddressesStyles';

const ImportAddresses = () => {
  const { states, countries } = useAddressForm();
  const {
    onDrop,
    importedAddresses,
    step,
    changeStep,
    onChangeFile,
    uploading,
    withSpinner,
    incorrectFormatMessage,
    setEditableAddress,
    editableAddress,
    downloadTemplate,
    onCancelUploading,
    apiError,
  } = useImportAddresses();
  const { onChangeSelect, onSaveAddress, onChangeInput, onCloseAddressForm } =
    useImportAddress(editableAddress, setEditableAddress);

  const { classes } = useStyles({
    firstStep: step === UPLOAD_STEP,
    incorrectFormatMessage,
  });
  const { onClose } = useModal();

  const acceptBtnTitle = useMemo(() => {
    if (step === UPLOAD_STEP) {
      return 'Next';
    }
    if (step === REVIEW_STEP && !!importedAddresses.addresses.length) {
      return 'Save';
    }
    if (step === REVIEW_STEP && !importedAddresses.addresses.length) {
      return 'Close';
    }
  }, [importedAddresses, step]);

  const stepContent = useMemo(() => {
    if (step === UPLOAD_STEP) {
      return (
        <FileSelection
          withSpinner={withSpinner}
          onCancelUploading={onCancelUploading}
          uploadingProgress={importedAddresses.uploadingProgress}
          importedAddresses={importedAddresses}
          onDrop={onDrop}
          onChangeFile={onChangeFile}
          incorrectFormatMessage={incorrectFormatMessage}
        />
      );
    }
    if (editableAddress) {
      return (
        <>
          <Typography variant="h3" mb={2} mt={2}>
            Edit The Shipping Address
          </Typography>
          <Form
            onChangeInput={onChangeInput}
            apiErrors={editableAddress.GMOP_errors_description}
            uspsError={
              editableAddress?.USPS_return_text ||
              editableAddress?.USPS_error_description
            }
            address={editableAddress.original_fields}
            states={states}
            countries={countries}
            onChangeSelect={onChangeSelect}
            onSubmit={onSaveAddress}>
            <FooterButtons
              acceptBtnType="submit"
              acceptTitle="Save"
              rejectTitle="Cancel"
              onReject={onCloseAddressForm}
            />
          </Form>
        </>
      );
    }
    return (
      <Addresses
        addresses={importedAddresses.addresses}
        setEditableAddress={setEditableAddress}
      />
    );
  }, [
    step,
    withSpinner,
    onCancelUploading,
    importedAddresses,
    onDrop,
    onChangeFile,
    incorrectFormatMessage,
    editableAddress,
    onChangeInput,
    states,
    countries,
    onChangeSelect,
    onSaveAddress,
    onCloseAddressForm,
    setEditableAddress,
  ]);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div
          className={classes.stepInfo}
          data-testid="import-addresses-step-label">{`Step ${step}/${IMPORT_STEPS_COUNT}`}</div>
        {step === UPLOAD_STEP && (
          <Button
            className={classes.downloadButton}
            variant="text"
            onClick={downloadTemplate}
            data-testid="download-template-button">
            <Feather
              printType="stroke"
              type="arrowDown"
              className={classes.headerIcon}
            />
            Download Template
          </Button>
        )}
      </div>
      {stepContent}
      {apiError && <ErrorMessage message={apiError} />}
      {!editableAddress &&
        (importedAddresses.file || step === REVIEW_STEP) &&
        !withSpinner && (
          <FooterButtons
            withPrevButton={
              step === UPLOAD_STEP || !!importedAddresses.addresses.length
            }
            onReject={onClose}
            rejectTitle="Cancel"
            acceptTitle={acceptBtnTitle}
            onAccept={changeStep}
            isLoading={uploading}
            className={classes.footerButtons}
          />
        )}
    </div>
  );
};

export default ImportAddresses;
